<script setup lang="ts">
import { Button } from '@albank/vue-ui-kit';
import { PresentationEntity } from '@/entities/interfaces/presentations';
import { ISO8601ToTime } from '@/helpers/isoToTime';
import { EventEntity } from '@/entities/interfaces/events';
import { getImageLink } from '@/helpers/imageLink';
import { isActualEvent } from '@/helpers/isActualEvent';
import { useModalStore } from '@/stores/signup.store';

defineProps<{ event: EventEntity; presentations: PresentationEntity[] }>();

const modalStore = useModalStore();
</script>

<template>
  <div class="presentations-list">
    <article class="presentation" v-for="item in presentations" :key="item.id">
      <time class="presentation__time">{{ ISO8601ToTime(item.time) }}</time>

      <div class="presentation__content">
        <h3>{{ item.name }}</h3>
        <div class="presentation__data" v-if="item.speakerFIO">
          <img
            v-if="item.imageId"
            :src="getImageLink(item.imageId)"
            :alt="item.name"
          />
          <div class="presentation__text">
            <p class="presentation__speaker-fio">{{ item.speakerFIO }}</p>
            <p class="presentation__speaker-info">{{ item.speakerInfo }}</p>
            <p class="presentation__description">{{ item.description }}</p>
          </div>
        </div>
      </div>
    </article>

    <Button
      size="large"
      @click="modalStore.toggleModal()"
      v-if="isActualEvent(event.plannedDateTime)"
    >
      Зарегистрироваться
    </Button>
  </div>
</template>

<style scoped lang="scss">
.presentations-list {
  gap: 40px;
  display: flex;
  flex-direction: column;

  a {
    width: 100%;
    button {
      width: 100%;
    }
  }
}

.presentation {
  gap: 20px;
  display: flex;
  padding: 60px 40px;
  border-radius: 60px;
  color: var(--color-mono-1100);
  background: var(--color-mono-10);

  @media screen and (max-width: 1024px) {
    padding: 32px;
    flex-direction: column;
  }

  &__time {
    padding: 6px 0;
    line-height: 25.6px;
    font-size: var(--font-size-l);
    font-weight: var(--font-weight-regular);
  }

  &__content {
    gap: 20px;
    display: flex;
    flex-direction: column;

    h3 {
      font-size: var(--font-size-lm);
      font-weight: var(--font-weight-bold);

      @media screen and (max-width: 600px) {
        font-size: var(--font-size-md);
      }
    }
  }

  &__data {
    gap: 20px;
    display: flex;
    align-items: center;

    @media screen and (max-width: 600px) {
      flex-direction: column;
      align-items: flex-start;
    }

    img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      border-radius: 40px;
      object-position: center top;
    }
  }

  &__text {
    gap: 10px;
    display: flex;
    flex-direction: column;
  }

  &__speaker-fio {
    font-size: var(--font-size-l);
    color: var(--color-mono-1100);
    font-weight: var(--font-weight-medium);
  }

  &__speaker-info {
    color: #035de3;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-light);
  }
}
</style>
